import type { CPCPhase } from '../../../../bridge/utils/CPCLiveMessages';

export const PHASE_WEIGHTS: Record<CPCPhase, number> = {
  LOOP_1_CPC: 0.3,
  LOOP_1B_RSI_MRC: 0.15,
  LOOP_1C_IIC: 0.1,
  LOOP_2_CPC: 0.3,
  LOOP_2_IIC: 0.15,
};

const PHASE_ORDER: CPCPhase[] = [
  'LOOP_1_CPC',
  'LOOP_1B_RSI_MRC',
  'LOOP_1C_IIC',
  'LOOP_2_CPC',
  'LOOP_2_IIC',
];

// Sum of phase weights for phases that run BEFORE `phase`.
const cumulativeWeightBefore = (phase: CPCPhase): number =>
  PHASE_ORDER.slice(0, PHASE_ORDER.indexOf(phase)).reduce(
    (sum, p) => sum + PHASE_WEIGHTS[p],
    0,
  );

// Combo-level ETA: anchored to comboStartedAt so the projection accounts
// for ALL phases run so far in the current combo, not just the current
// phase. Formula: totalEstimated = elapsed / cumulativeFraction;
// ETA = totalEstimated - elapsed. This handles the second bar's "entire
// combo" semantics.
//
// Loop 2 is iterative (multi-pass). For pass N, we treat the remaining
// Loop 2 budget as larger by a passOvershoot factor so we don't claim the
// combo is "done" after a single Loop 2 pass.
export const computeComboETAMs = (input: {
  nowMs: number;
  comboStartedAt: number;
  phase: CPCPhase;
  phaseDone: number;
  phaseTotal: number;
  passIndex?: number | null;
}): number | null => {
  if (input.phaseTotal === 0 || input.phaseDone === 0) return null;
  const elapsed = input.nowMs - input.comboStartedAt;
  if (elapsed <= 0) return null;

  const phaseFraction = input.phaseDone / input.phaseTotal;
  let cumulative =
    cumulativeWeightBefore(input.phase) +
    phaseFraction * PHASE_WEIGHTS[input.phase];

  // Loop 2 multi-pass adjustment. Without it, cumulative reaches its phase
  // ceiling after pass 1 even though more passes will run. Scale the
  // "Loop 2 progress" component down to leave headroom for future passes.
  if (
    (input.phase === 'LOOP_2_CPC' || input.phase === 'LOOP_2_IIC') &&
    input.passIndex != null &&
    input.passIndex >= 1
  ) {
    const loop1Weight =
      PHASE_WEIGHTS.LOOP_1_CPC +
      PHASE_WEIGHTS.LOOP_1B_RSI_MRC +
      PHASE_WEIGHTS.LOOP_1C_IIC;
    const loop2Weight = PHASE_WEIGHTS.LOOP_2_CPC + PHASE_WEIGHTS.LOOP_2_IIC;
    // Assume the typical combo runs 3 Loop 2 passes; divide the Loop 2
    // weight across those passes.
    const expectedPasses = 3;
    const subPhaseWeight =
      input.phase === 'LOOP_2_CPC'
        ? PHASE_WEIGHTS.LOOP_2_CPC
        : PHASE_WEIGHTS.LOOP_2_IIC;
    const completedLoop2Weight =
      // Previous passes' contribution.
      ((input.passIndex - 1) / expectedPasses) * loop2Weight +
      // Current pass's previous sub-phases (CPC done if we're in IIC).
      (input.phase === 'LOOP_2_IIC'
        ? PHASE_WEIGHTS.LOOP_2_CPC / expectedPasses
        : 0) +
      // Current sub-phase's progress.
      (phaseFraction * subPhaseWeight) / expectedPasses;
    cumulative = loop1Weight + completedLoop2Weight;
  }

  // 0.999 instead of 1 to absorb floating-point imprecision when phase
  // weights sum exactly to 1.
  if (cumulative <= 0 || cumulative >= 0.999) return null;
  return elapsed * (1 / cumulative - 1);
};

const lastN = (xs: number[], n: number): number[] =>
  xs.slice(Math.max(0, xs.length - n));

export const computeOverallETAMs = (input: {
  comboIndex: number;
  comboTotal: number;
  finishedDurations: number[];
  fallbackTotalDurationMs: number | null;
  currentComboETAMs: number | null;
}): number | null => {
  const remainingCombos = Math.max(0, input.comboTotal - input.comboIndex - 1);
  let avgComboMs: number | null = null;
  if (input.finishedDurations.length > 0) {
    const last = lastN(input.finishedDurations, 3);
    avgComboMs = last.reduce((s, d) => s + d, 0) / last.length;
  } else if (input.fallbackTotalDurationMs && input.comboTotal > 0) {
    avgComboMs = input.fallbackTotalDurationMs / input.comboTotal;
  }
  if (avgComboMs == null) return input.currentComboETAMs;
  return avgComboMs * remainingCombos + (input.currentComboETAMs ?? 0);
};
